import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/api';
import { Card, Button, Input, Select, Label, Icon } from '../../components/ui';

export default function GeneralLedger() {
  const [accountCode, setAccountCode] = useState('');
  const [periodKey, setPeriodKey] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState([]);
  const [viewMode, setViewMode] = useState('detail');

  useEffect(() => { loadAccounts(); }, []);

  const loadAccounts = async () => {
    try {
      const res = await api.get('/chart-of-accounts?active=true&limit=500');
      setAccounts(res.data?.accounts || []);
    } catch { setAccounts([]); }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (accountCode) params.accountCode = accountCode;
      if (periodKey) params.periodKey = periodKey;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      if (viewMode === 'detail') {
        const res = await api.get('/accounting/general-ledger', { params });
        setData(res.data);
      } else {
        const res = await api.get('/accounting/general-ledger/summary', { params });
        setSummary(res.data?.accounts || []);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (accountCode) params.set('accountCode', accountCode);
      if (periodKey) params.set('periodKey', periodKey);
      if (fromDate) params.set('fromDate', fromDate);
      if (toDate) params.set('toDate', toDate);

      const res = await api.get(`/accounting/export/general-ledger?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `grand_livre_${accountCode || 'tous'}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel téléchargé');
    } catch (e) {
      toast.error('Erreur export');
    }
  };

  const exportPdf = async () => {
    try {
      const params = new URLSearchParams();
      if (accountCode) params.set('accountCode', accountCode);
      if (periodKey) params.set('periodKey', periodKey);
      if (fromDate) params.set('fromDate', fromDate);
      if (toDate) params.set('toDate', toDate);
      const res = await api.get(`/accounting/export/general-ledger/pdf?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `grand_livre_${accountCode || 'tous'}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export PDF téléchargé');
    } catch { toast.error('Erreur export PDF'); }
  };

  const exportWord = async () => {
    try {
      const params = new URLSearchParams();
      if (accountCode) params.set('accountCode', accountCode);
      if (periodKey) params.set('periodKey', periodKey);
      if (fromDate) params.set('fromDate', fromDate);
      if (toDate) params.set('toDate', toDate);
      const res = await api.get(`/accounting/export/general-ledger/word?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `grand_livre_${accountCode || 'tous'}.docx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export Word téléchargé');
    } catch { toast.error('Erreur export Word'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(v || 0);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          <Icon name="IconBook" className="text-indigo-600 mr-3" />
          Grand Livre
        </h1>
        <p className="text-sm text-gray-600 mt-1">Consultation du Grand Livre par compte et par période.</p>
      </div>

      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Compte</Label>
            <Select value={accountCode} onChange={(e) => setAccountCode(e.target.value)}>
              <option value="">Tous les comptes</option>
              {accounts.map((a) => (
                <option key={a.code} value={a.code}>{a.code} - {a.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Période (YYYY-MM)</Label>
            <Input value={periodKey} onChange={(e) => setPeriodKey(e.target.value)} placeholder="2026-02" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Du</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Au</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Label className="text-xs text-gray-500">Mode</Label>
            <Select value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
              <option value="detail">Détail</option>
              <option value="summary">Résumé</option>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={loadData} disabled={loading}>
              {loading ? 'Chargement...' : 'Consulter'}
            </Button>
            <Button onClick={exportExcel} variant="secondary">Excel</Button>
            <Button onClick={exportPdf} variant="secondary">PDF</Button>
            <Button onClick={exportWord} variant="secondary">Word</Button>
          </div>
        </div>
      </Card>

      {viewMode === 'detail' && data && (
        <Card>
          {data.account && (
            <div className="mb-4 p-3 bg-indigo-50 rounded-lg">
              <span className="font-bold text-indigo-800">Compte: {data.account.code}</span>
              <span className="text-indigo-600 ml-2">- {data.account.label}</span>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Date</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Journal</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">N°</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Libellé</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Débit</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Crédit</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Solde</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.lines?.length === 0 ? (
                  <tr><td colSpan={7} className="text-center text-gray-500 py-8">Aucune écriture</td></tr>
                ) : (
                  data.lines?.map((l, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm">{l.entryDate ? new Date(l.entryDate).toLocaleDateString('fr-FR') : ''}</td>
                      <td className="px-3 py-2 text-sm">{l.journalCode}</td>
                      <td className="px-3 py-2 text-sm">{l.entryNumber}</td>
                      <td className="px-3 py-2 text-sm max-w-xs truncate">{l.label || l.entryLabel}</td>
                      <td className="px-3 py-2 text-sm text-right">{l.debit ? formatCurrency(l.debit) : ''}</td>
                      <td className="px-3 py-2 text-sm text-right">{l.credit ? formatCurrency(l.credit) : ''}</td>
                      <td className="px-3 py-2 text-sm text-right font-semibold">{formatCurrency(l.runningBalance)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="font-bold bg-gray-100">
                  <td colSpan={4} className="px-3 py-2">TOTAUX</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(data.totals?.debit)}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(data.totals?.credit)}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(data.totals?.balance)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}

      {viewMode === 'summary' && summary.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Compte</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Libellé</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Débit</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Crédit</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Solde</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Lignes</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {summary.map((a) => (
                  <tr key={a._id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm font-mono">{a._id}</td>
                    <td className="px-3 py-2 text-sm">{a.label}</td>
                    <td className="px-3 py-2 text-sm text-right">{formatCurrency(a.totalDebit)}</td>
                    <td className="px-3 py-2 text-sm text-right">{formatCurrency(a.totalCredit)}</td>
                    <td className="px-3 py-2 text-sm text-right font-semibold">{formatCurrency(a.balance)}</td>
                    <td className="px-3 py-2 text-sm text-right">{a.linesCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
